import React from 'react';

const Header = ({
  user,
  subscriptionData,
  isMobile,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  setIsGlobalSearchOpen,
  showProfileDropdown,
  setShowProfileDropdown,
  setShowProfileModal,
  setShowSubscriptionModal,
  setShowHelpModal,
  fetchSubscriptionData,
  handleLogout,
  showMessage,
}) => {
  const userInitials = (user?.username || 'AD').slice(0, 2).toUpperCase();
  const organizationName = user?.client_name || subscriptionData?.organization_name || 'Nexius Digital';
  const planName =
    subscriptionData?.plan_name ||
    user?.plan_name ||
    (user?.role === 'superadmin' ? 'Master Platform Node' : 'Professional Plan');

  return (
    <header className="bg-white border-b border-slate-200/80 shadow-xs sticky top-0 z-40">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center h-16 px-4 md:px-6 gap-4">
          {/* Left: Mobile Menu Trigger & Organization Badge */}
          <div className="flex items-center gap-3 min-w-0">
            {isMobile && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-600 hover:text-slate-900 p-2 rounded-xl hover:bg-slate-100 focus:outline-none transition-colors duration-200 flex-shrink-0"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-xs flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-base md:text-lg font-bold text-slate-800 tracking-tight truncate">
                  {organizationName}
                </h2>
              </div>
            </div>
          </div>

          {/* Center: Command Palette Search Bar (Desktop) */}
          <div className="hidden md:flex flex-1 max-w-md mx-4">
            <div
              onClick={() => setIsGlobalSearchOpen(true)}
              className="w-full bg-slate-50 hover:bg-slate-100/80 cursor-pointer border border-slate-200/80 hover:border-emerald-300 rounded-xl px-3.5 py-1.5 flex items-center gap-2.5 transition-all shadow-2xs group"
            >
              <svg
                className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 flex-shrink-0 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <span className="w-full text-xs md:text-sm text-slate-400 group-hover:text-slate-600 select-none truncate">
                Search tickets, assets, serials...
              </span>
              <span className="text-[10px] font-semibold text-slate-400 group-hover:text-emerald-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded shadow-2xs flex-shrink-0 transition-colors">
                ⌘K
              </span>
            </div>
          </div>

          {/* Right: Notifications & Account Dropdown */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* Notification Bell */}
            <button
              onClick={() => {
                if (showMessage) showMessage('No new unread notifications', 'info');
              }}
              className="w-9 h-9 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 flex items-center justify-center relative transition-colors"
              title="Notifications"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-white"></span>
            </button>

            {/* User Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowProfileDropdown((v) => !v);
                  if (!subscriptionData && fetchSubscriptionData) {
                    fetchSubscriptionData();
                  }
                }}
                className="flex items-center gap-2.5 p-1 md:pr-3 rounded-full hover:bg-slate-100 focus:outline-none transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white font-bold text-xs flex items-center justify-center shadow-xs">
                  {userInitials}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-xs md:text-sm font-bold text-slate-800 leading-tight truncate max-w-[120px]">
                    {user?.username || 'Admin'}
                  </p>
                  <p className="text-[10px] font-semibold text-slate-400 tracking-wider uppercase leading-tight mt-0.5">
                    {(user?.role || 'Admin').toUpperCase()}
                  </p>
                </div>
                <svg className="hidden md:block w-4 h-4 text-slate-400 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-80 sm:w-[330px] bg-white border border-slate-200/90 rounded-2xl shadow-2xl z-50 overflow-hidden animate-fadeIn text-slate-800">
                  {/* 1. Header: User Identity Box */}
                  <div className="p-4 flex items-center gap-3.5 bg-white">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white font-bold text-lg flex items-center justify-center shadow-xs flex-shrink-0">
                      {userInitials}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-slate-900 truncate">
                          {user?.username || 'Admin'}
                        </h4>
                        <span className="bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider">
                          {(user?.role || 'Admin').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
                        {user?.email || subscriptionData?.contact_email || 'ajay@nexius.in'}
                      </p>
                      <div className="text-[11px] text-slate-400 font-medium flex items-center gap-1.5 truncate mt-1">
                        <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="truncate">{organizationName}</span>
                      </div>
                    </div>
                  </div>

                  {/* 2. Subscription Plan Card */}
                  <div className="mx-3.5 my-1.5 p-3.5 rounded-2xl bg-slate-50 border border-slate-200/70 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>SUBSCRIPTION PLAN</span>
                      </div>
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Active
                      </span>
                    </div>

                    <div className="text-base font-extrabold text-slate-900">
                      {planName}
                    </div>

                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowSubscriptionModal(true);
                        if (fetchSubscriptionData) fetchSubscriptionData();
                      }}
                      className="w-full bg-white hover:bg-slate-100 border border-slate-200/80 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 flex items-center justify-between shadow-2xs transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <span>Manage Subscription & Invoices</span>
                      </div>
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* 3. Navigation & Actions List */}
                  <div className="py-1 border-t border-slate-100/80 space-y-0.5">
                    {/* Profile Settings */}
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowProfileModal(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <span>Profile Settings</span>
                    </button>

                    {/* Subscription & Transactions */}
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowSubscriptionModal(true);
                        if (fetchSubscriptionData) fetchSubscriptionData();
                      }}
                      className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <span>Subscription & Transactions</span>
                    </button>

                    {/* Email Support */}
                    <a
                      href="mailto:support@nexbond.in?subject=NexDesk%20Support%20Request"
                      className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 font-semibold leading-tight">Email Support</div>
                        <div className="text-[11px] text-slate-400 font-normal truncate mt-0.5">support@nexbond.in</div>
                      </div>
                    </a>

                    {/* Help & Documentation */}
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowHelpModal(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span>Help & Documentation</span>
                    </button>
                  </div>

                  {/* 4. Sign Out */}
                  <div className="border-t border-slate-100 pt-1">
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setTimeout(handleLogout, 100);
                      }}
                      className="w-full text-left px-4 py-3 text-xs font-bold text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
